import { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Person } from 'types';
import PeopleEditor from 'views/Widgets/Forms/PeopleEditor';
import AddReviewersFromFilter from './AddReviewersFromFilter';

interface Props {
  // Form field value and change handler (from ValidatedInput CUSTOM_COMPONENT)
  value?: Person[];
  onChange?: (people: Person[]) => void;
  onValidChange?: (message: string | null) => void;
  // PeopleEditor passthrough props
  name?: string;
  placeholder?: string;
  allowSelf?: boolean;
  required?: boolean;
  minSelections?: number;
  maxSelections?: number;
  disabled?: boolean;
  isSubmitting?: boolean;
  excludeList?: Person[];
  autoFocus?: boolean;
  inline?: boolean;
  inForm?: boolean;
  suggestions?: object[];
  elasticsearchOptions?: object;
  excludeListErrorMessage?: string;
  showInviteInstructions?: boolean;
  showInviteSuggestions?: boolean;
  showPlaceholderAfterFirstTag?: boolean;
  showSuggestionsAsPopover?: boolean;
  maxSuggestions?: number;
}

const ReviewersFieldWithFilter: FC<Props> = ({
  value,
  onChange,
  onValidChange,
  disabled,
  isSubmitting,
  ...peopleEditorProps
}) => {
  const [showFilter, setShowFilter] = useState(false);

  const handlePeopleEditorChange = useCallback(
    (people: Person[]) => {
      if (!onChange) return;
      onChange(people);
    },
    [onChange]
  );

  const handleAddFromFilter = useCallback(
    (newPeople: Person[]) => {
      if (!onChange) return;
      const existing = value ?? [];
      const existingIds = new Set(existing.map((p) => p.id));
      const uniqueNew = newPeople.filter((p) => !existingIds.has(p.id));
      onChange([...existing, ...uniqueNew]);
      setShowFilter(false);
    },
    [onChange, value]
  );

  const handleCloseFilter = useCallback(() => {
    setShowFilter(false);
  }, []);

  const excludePersonIds = (value ?? [])
    .map((p) => p.id)
    .filter((id): id is number => id != null);

  return (
    <div>
      <PeopleEditor
        {...peopleEditorProps}
        value={value ?? []}
        callback={handlePeopleEditorChange}
        onValidChange={onValidChange}
        disabled={disabled || isSubmitting}
      />
      {!showFilter && (
        <div className="mt-1">
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            style={{ fontSize: '0.875rem' }}
            onClick={() => setShowFilter(true)}
          >
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.components.reviewers_field_with_filter.add_from_filter"
              defaultMessage="Add from filter"
            />
          </button>
        </div>
      )}
      {showFilter && (
        <AddReviewersFromFilter
          excludePersonIds={excludePersonIds}
          onAddPeople={handleAddFromFilter}
          onClose={handleCloseFilter}
        />
      )}
    </div>
  );
};

export default ReviewersFieldWithFilter;
